import { type ReactElement, type ReactNode, useEffect, useState } from "react";
import { FormattedMessage } from "react-intl";
import { useSearchParams } from "react-router-dom";
import classNames from "classnames";

import { signIntoDigicert } from "common/account/login/digicert";
import Icon from "common/core/icon";
import TextField from "common/form/fields/text";
import Button from "common/core/button";
import { useMutation } from "util/graphql";
import { CertificateValidationStatusEnum, DigicertValidationActions } from "graphql_globals";
import ActionButton from "common/core/action_button";
import { segmentTrack } from "util/segment";
import { useCompanyName } from "util/feature_detection";
import { supportEmail } from "constants/support";

import TriggerDigicertValidationMutation from "./trigger_digicert_validation_mutation.graphql";
import type { SubmitType } from "../../section_utils";
import type { NotaryProfileWizardDigitalCertificate as User } from "../index_fragment.graphql";
import Styles from "./index.module.scss";

const digicertUrl = "https://one.digicert.com";
type Props = {
  notaryProfileId: string;
  validationStatus: CertificateValidationStatusEnum;
  digicertConnected: boolean;
  email: string | null;
  renderFooter: (handleSubmit: () => SubmitType, disabled: boolean) => ReactNode;
  onNext: (input: null, skipConfig?: "DontNavigate") => Promise<User>;
};

type ValidationState = "none" | "loading" | "sent" | "error";

function getAuthorizationErrorMessage(
  digicertAuthorizationError: string | null,
  companyName: string,
): ReactElement | null {
  switch (digicertAuthorizationError) {
    case "unauthorized":
      return (
        <FormattedMessage
          id="0291c1cc-c5f7-4313-b01f-38d439f8ff92"
          defaultMessage="{companyName} was unable to connect to your DigiCert account. Please verify you have completed the DigiCert identity verification and have accepted DigiCert's Terms of service, by logging into your DigiCert account at {link}."
          values={{
            companyName,
            link: (
              <a href={digicertUrl} target="_blank" rel="noreferrer noopener">
                {digicertUrl}
              </a>
            ),
          }}
        />
      );
    case "user_not_found":
      return (
        <FormattedMessage
          id="a63488a1-a7f9-494a-919a-e86097fb0bb2"
          defaultMessage="Your authorization request timed out. Please try again or contact support if the problem persists. {link}"
          values={{
            link: <a href={`mailto:${supportEmail}`}>{supportEmail}</a>,
          }}
        />
      );
    case "invalid_certificate":
      return (
        <FormattedMessage
          id="594ff496-51aa-46f4-8d5f-38de295d7025"
          defaultMessage="Your DigiCert certificate is invalid. Please contact support. {link}"
          values={{
            link: <a href={`mailto:${supportEmail}`}>{supportEmail}</a>,
          }}
        />
      );
    case "unexpected_error":
      return (
        <FormattedMessage
          id="0862bd90-c0bf-483d-836a-73f1148a01a7"
          defaultMessage="An unexpected error occurred. Please try again or contact support if the problem persists. {link}"
          values={{
            link: <a href={`mailto:${supportEmail}`}>{supportEmail}</a>,
          }}
        />
      );
    default:
      return null;
  }
}

export function DigicertDigitalCertificate({
  notaryProfileId,
  validationStatus,
  digicertConnected,
  email,
  renderFooter,
  onNext,
}: Props) {
  const [searchParams] = useSearchParams();
  const notaryTriggeredValidation =
    validationStatus !== CertificateValidationStatusEnum.NOT_STARTED;
  const pendingIdentityProofing =
    validationStatus === CertificateValidationStatusEnum.PENDING_IDENTITY_PROOFING;
  const hasValidationError = validationStatus === CertificateValidationStatusEnum.VALIDATION_ERROR;

  const [emailValidationState, setEmailValidationState] = useState<ValidationState>(
    validationStatus === CertificateValidationStatusEnum.REJECTED ||
      validationStatus === CertificateValidationStatusEnum.ID_ERROR
      ? "error"
      : "none",
  );

  const notaryCertificateValidated = validationStatus === CertificateValidationStatusEnum.APPROVED;
  const notaryCertificateRejected =
    validationStatus === CertificateValidationStatusEnum.REJECTED ||
    validationStatus === CertificateValidationStatusEnum.ID_ERROR;
  const [emailSentBannerOpen, setEmailSentBannerOpen] = useState(
    notaryTriggeredValidation &&
      !notaryCertificateValidated &&
      !notaryCertificateRejected &&
      !hasValidationError,
  );
  const [showEmailErrorMessage, setShowEmailErrorMessage] = useState(
    validationStatus === CertificateValidationStatusEnum.REJECTED ||
      validationStatus === CertificateValidationStatusEnum.ID_ERROR,
  );
  const authorizationErrorMessage = getAuthorizationErrorMessage(
    searchParams.get("digicert_authorization_error"),
    useCompanyName(),
  );

  const [alreadyResentEmail, setAlreadyResentEmail] = useState(false);
  const [canReattemptValidation, setCanReattemptValidation] = useState(true);

  useEffect(() => {
    if (!canReattemptValidation) {
      const timeoutId = setTimeout(() => setCanReattemptValidation(true), 60_000);
      return () => clearTimeout(timeoutId);
    }
  }, [canReattemptValidation]);

  const triggerDigicertValidationMutationFn = useMutation(TriggerDigicertValidationMutation);

  const triggerValidation = () => {
    setShowEmailErrorMessage(false);
    setEmailValidationState("loading");
    triggerDigicertValidationMutationFn({
      variables: {
        input: { notaryProfileId, attemptToCancel: alreadyResentEmail },
      },
    })
      .then((response) => {
        const firstCredentialRestarted =
          response.data?.triggerDigicertValidation?.actionTaken ===
          DigicertValidationActions.RESTART_FIRST_CREDENTIAL;
        if (!firstCredentialRestarted) {
          setEmailValidationState("sent");
          setEmailSentBannerOpen(true);
        }
        setCanReattemptValidation(false);
        const alreadySent =
          response.data?.triggerDigicertValidation?.actionTaken ===
          DigicertValidationActions.RESEND_EMAIL;
        setAlreadyResentEmail(alreadySent);
      })
      .catch((error) => {
        setShowEmailErrorMessage(true);
        setEmailValidationState("error");
        segmentTrack("DIGICERT_VALIDATION_ERROR", error);
      });
  };

  return (
    <>
      {emailSentBannerOpen && (
        <div className={Styles.banner}>
          <Icon name="success" size="large" />
          <FormattedMessage
            id="6775736c-ff15-49f9-95be-ac2a6b271f53"
            defaultMessage="DigiCert sent an email to {email}. It may take a minute to arrive."
            values={{ email }}
          />
          <ActionButton onClick={() => setEmailSentBannerOpen(false)}>
            <Icon name="x-small" size="large" />
          </ActionButton>
        </div>
      )}
      <div>
        <FormattedMessage
          id="a2a57b97-2618-42d1-b67a-a4f125510dd7"
          defaultMessage="Digital Certificate"
          tagName="h3"
        />
        <FormattedMessage
          id="6c19ec49-505b-48e1-a67b-58afe1eb3a32"
          defaultMessage="Your account security is important to us. That’s why the Notarize Network is now compatible with DigiCert digital certificates. Learn more about Digicert."
          tagName="strong"
        />
        <div className={Styles.step}>
          <div className={Styles.icon}>
            {notaryTriggeredValidation && !showEmailErrorMessage ? (
              <Icon name="success" />
            ) : (
              <div>◯</div>
            )}
            <div className={Styles.statusBar} />
          </div>
          <div className={Styles.stepContent}>
            <FormattedMessage
              id="7ee0ca86-837c-4606-959c-692de0276815"
              defaultMessage="Step 1"
              tagName="strong"
            />
            <FormattedMessage
              id="f7198ace-14e3-4611-aa45-f3d72015e496"
              defaultMessage="To get started, the DigiCert team will send you an email to verify your identity. Confirm the following email is correct. "
            />
            <div className={Styles.action}>
              <FormattedMessage
                id="2b6ff7c7-0f6b-4542-8550-ce082db96359"
                defaultMessage="Follow the directions found in the email. Once Digicert can verify your identity, we will email you to continue onboarding."
              />
              <TextField
                id="email"
                name="email"
                automationId="digicertEmail"
                useStyledInput
                disabled
                className={Styles.email}
              />
              {showEmailErrorMessage && (
                <div className={Styles.error}>
                  <FormattedMessage
                    id="6a051bd1-fafa-4de9-8275-589c6ca16640"
                    defaultMessage="Digicert was unable to verify your identity. Please resend the email and try again."
                  />
                </div>
              )}

              <Button
                buttonColor="action"
                variant="primary"
                isLoading={emailValidationState === "loading"}
                disabled={
                  notaryCertificateValidated || !canReattemptValidation || pendingIdentityProofing
                }
                onClick={triggerValidation}
              >
                <FormattedMessage
                  id="6e663030-43a1-416f-b8c3-19df26289bbe"
                  defaultMessage="{notaryTriggeredValidation, select, true {Reattempt} other {Send email}}"
                  values={{ notaryTriggeredValidation }}
                />
              </Button>
            </div>
          </div>
        </div>
        <div className={classNames(Styles.step, !notaryCertificateValidated && Styles.disabled)}>
          <div className={classNames(Styles.icon, !notaryCertificateValidated && Styles.disabled)}>
            {digicertConnected ? <Icon name="success" /> : <div>◯</div>}
            <div className={Styles.statusBar} />
          </div>
          <div className={Styles.stepContent}>
            <FormattedMessage
              id="17800262-d141-43aa-b2ee-aba9ba0998b1"
              defaultMessage="Step 2"
              tagName="strong"
            />
            <FormattedMessage
              id="73cacebb-9cdc-4439-adb7-a9b0e94d67d8"
              defaultMessage="Sign into your Digicert account below."
            />

            <div className={Styles.action}>
              <Button
                buttonColor="action"
                variant="primary"
                onClick={signIntoDigicert}
                disabled={!notaryCertificateValidated || digicertConnected}
              >
                <FormattedMessage
                  id="48d4d9ca-f4f0-4b67-bf7d-12b6f506c921"
                  defaultMessage="Sign into digicert"
                />
              </Button>
              {authorizationErrorMessage && (
                <div className={Styles.authorizationError}>{authorizationErrorMessage}</div>
              )}
            </div>
          </div>
        </div>
        <div className={classNames(Styles.step, !digicertConnected && Styles.disabled)}>
          <div className={classNames(Styles.icon, !digicertConnected && Styles.disabled)}>
            {digicertConnected ? <Icon name="success" /> : <div>◯</div>}
          </div>
          <div className={Styles.stepContent}>
            <FormattedMessage
              id="a5616612-4185-41a4-b2ec-c21e27888624"
              defaultMessage="Step 3"
              tagName="strong"
            />
            <FormattedMessage
              id="08cb69a6-dd68-4e90-94a7-405062b5306e"
              defaultMessage="Your digital certificate is all set!"
            />
          </div>
        </div>
      </div>
      {renderFooter(() => onNext(null), !digicertConnected)}
    </>
  );
}
